import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemDescription,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn, useOverlayTriggerTooltip } from '@heidi/utils';
import { ChevronsRight, Hand, type LucideIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';
import type { ApprovalMode } from '../../contracts/harness.types';
import {
  acknowledgeAutoApproveDisclaimer,
  hasAcknowledgedAutoApproveDisclaimer,
} from '../../data/autoApproveDisclaimerAcknowledgement';
import { composerToolbarLabeledButtonClassName } from '../../data/projects/components/composer-toolbar-icon-button';
import { AutoApproveDisclaimerDialog } from './AutoApproveDisclaimerDialog';

const messages = defineMessages({
  manualShortLabel: {
    defaultMessage: 'Ask',
    id: 'AF1//+V3to',
    description: 'Composer approval-mode trigger when every action is gated',
  },
  manualLabel: {
    defaultMessage: 'Ask for approval',
    id: 'rjaENGGvRI',
    description: 'Composer approval-mode menu row when every action is gated',
  },
  manualDescription: {
    defaultMessage: 'You approve every action before Heidi continues',
    id: '+mTxICzb/p',
    description: 'Composer approval-mode caption for Ask for approval',
  },
  autoShortLabel: {
    defaultMessage: 'Approve',
    id: 'dae0r7SmUw',
    description: 'Composer approval-mode trigger when ordinary steps auto-run',
  },
  autoLabel: {
    defaultMessage: 'Approve for me',
    id: '/FJQS9guqE',
    description: 'Composer approval-mode menu row when ordinary steps auto-run',
  },
  autoDescription: {
    defaultMessage:
      'Sensitive actions need your approval. Other steps run automatically.',
    id: 'ScYisFpqhn',
    description: 'Composer approval-mode caption for Approve for me',
  },
  triggerAriaLabel: {
    defaultMessage: 'Approval mode',
    id: 'k4O1Rg2Cmc',
  },
  triggerTooltip: {
    defaultMessage: 'Approval mode',
    id: '4aoq2zl+qN',
    description: 'Composer approval-mode trigger tooltip',
  },
});

const OPTIONS: readonly {
  mode: ApprovalMode;
  icon: LucideIcon;
  shortLabel: MessageDescriptor;
  label: MessageDescriptor;
  description: MessageDescriptor;
}[] = [
  {
    mode: 'manual',
    icon: Hand,
    shortLabel: messages.manualShortLabel,
    label: messages.manualLabel,
    description: messages.manualDescription,
  },
  {
    mode: 'auto',
    icon: ChevronsRight,
    shortLabel: messages.autoShortLabel,
    label: messages.autoLabel,
    description: messages.autoDescription,
  },
];

interface ProjectApprovalModeSelectorProps {
  value: ApprovalMode;
  onChange: (next: ApprovalMode) => void;
  disabled?: boolean;
  className?: string;
}

// Auto never suppresses approval UI — server-side hard floors still gate.
export function ProjectApprovalModeSelector({
  value,
  onChange,
  disabled = false,
  className,
}: ProjectApprovalModeSelectorProps): JSX.Element {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [autoDisclaimerOpen, setAutoDisclaimerOpen] = useState(false);
  const idPrefix = useId();
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();
  const { open: tooltipOpen, onOpenChange: setTooltipOpen } = tooltipProps;
  const selected = OPTIONS.find((option) => option.mode === value) ?? OPTIONS[0]!;

  const handleModeChange = (next: string) => {
    const nextMode = next as ApprovalMode;
    if (nextMode === value) return;
    if (nextMode === 'auto' && !hasAcknowledgedAutoApproveDisclaimer()) {
      setAutoDisclaimerOpen(true);
      return;
    }
    onChange(nextMode);
  };

  const selector = (
    <DropdownMenu
      // Non-modal like the toolbar's `+` and Sources popovers. A modal menu sends
      // every outside press to <html>, which the compact composer treats as a
      // click away, collapsing it under the open menu.
      modal={false}
      open={menuOpen}
      onOpenChange={(next) => {
        setMenuOpen(next);
        if (next) setTooltipOpen(false);
      }}
    >
      <Tooltip open={menuOpen ? false : tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <Button
              type="button"
              variant="ghost"
              size="md"
              disableAutomaticTypographyClasses
              disabled={disabled}
              aria-label={intl.formatMessage(messages.triggerAriaLabel)}
              data-testid="project-approval-mode-trigger"
              className={cn(composerToolbarLabeledButtonClassName, className)}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <selected.icon className="size-4 shrink-0" aria-hidden />
                <span className="truncate">
                  {intl.formatMessage(selected.shortLabel)}
                </span>
              </span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {intl.formatMessage(messages.triggerTooltip)}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="w-72"
        data-testid="project-approval-mode-menu"
        onCloseAutoFocus={contentProps.onCloseAutoFocus}
      >
        <DropdownMenuRadioGroup value={value} onValueChange={handleModeChange}>
          {OPTIONS.map((option) => {
            // RadioItem has no `description` prop, so mirror DropdownMenuItem's
            // name/description split by hand.
            const labelId = `${idPrefix}-${option.mode}-label`;
            const descriptionId = `${idPrefix}-${option.mode}-description`;
            return (
              <DropdownMenuRadioItem
                key={option.mode}
                value={option.mode}
                data-testid={`project-approval-mode-${option.mode}`}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                className="group items-start"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div id={labelId} className="flex items-center gap-2">
                    <option.icon
                      className="size-4 shrink-0 text-text-secondary"
                      aria-hidden
                    />
                    {intl.formatMessage(option.label)}
                  </div>
                  <DropdownMenuItemDescription
                    id={descriptionId}
                    className="ps-6 text-xs"
                  >
                    {intl.formatMessage(option.description)}
                  </DropdownMenuItemDescription>
                </div>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {selector}
      <AutoApproveDisclaimerDialog
        open={autoDisclaimerOpen}
        onOpenChange={setAutoDisclaimerOpen}
        onConfirm={() => {
          acknowledgeAutoApproveDisclaimer();
          onChange('auto');
        }}
      />
    </>
  );
}
